import React, { useState } from 'react';
import { FaFacebookF, FaWhatsapp, FaInstagram, FaLink } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';

interface ShareButtonsProps {
  title?: string;
}

// Wrapper to bypass TS2786 JSX return type mismatch for older TS versions
const Icon = ({ as: Component, ...props }: any) => <Component {...props} />;

const ShareButtons: React.FC<ShareButtonsProps> = ({ title }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  
  // Try to use window.location if available
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = title || (typeof document !== 'undefined' ? document.title : '');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link: ', err);
    }
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareTitle)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + currentUrl)}`,
    instagram: `https://www.instagram.com/` // Fallback to IG homepage
  };

  return (
    <div className="flex items-center space-x-3 py-6 mt-4 border-t border-gray-100">
      <span className="text-sm font-bold text-gray-400 uppercase tracking-widest mr-2">{t('share.title')}</span>
      
      <a 
        href={shareLinks.twitter} 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-black hover:text-white transition-all shadow-sm"
        title="X'te Paylaş"
      >
        <Icon as={FaXTwitter} className="w-4 h-4" />
      </a>

      <a 
        href={shareLinks.facebook} 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-[#1877F2] hover:text-white transition-all shadow-sm"
        title="Facebook'ta Paylaş"
      >
        <Icon as={FaFacebookF} className="w-4 h-4" />
      </a>

      <a 
        href={shareLinks.whatsapp} 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-[#25D366] hover:text-white transition-all shadow-sm"
        title="WhatsApp'ta Paylaş"
      >
        <Icon as={FaWhatsapp} className="w-4 h-4" />
      </a>

      <a 
        href={shareLinks.instagram} 
        target="_blank" 
        rel="noopener noreferrer"
        className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-gradient-to-tr hover:from-[#f09433] hover:via-[#e6683c] hover:to-[#bc1888] hover:text-white transition-all shadow-sm"
        title="Instagram"
      >
        <Icon as={FaInstagram} className="w-4 h-4" />
      </a>

      <button 
        onClick={handleCopyLink}
        className="relative w-10 h-10 flex items-center justify-center rounded-full bg-gray-50 text-gray-500 hover:bg-brand-purple-900 hover:text-white transition-all shadow-sm group"
        title={t('share.copy_link') as string}
      >
        <Icon as={FaLink} className="w-4 h-4" />
        {copied && (
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap animate-in fade-in slide-in-from-bottom-2">
            {t('share.copied')}
          </span>
        )}
      </button>

    </div>
  );
};

export default ShareButtons;
